import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { getPaymentRefunds } from '../../actions/refund';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Form, 
    Alert, 
    Spinner, 
    Table, 
    Badge,
    InputGroup
} from 'react-bootstrap';
import { 
    ArrowLeft,
    RefreshCw,
    Search,
    IndianRupee,
    Calendar,
    FileText,
    CheckCircle,
    Clock,
    XCircle,
    CreditCard,
    Filter,
    Eye
} from 'lucide-react';

const PaymentRefunds = ({ 
    getPaymentRefunds, 
    paymentRefunds, 
    loading, 
    error,
    pagination,
    filters
}) => {
    const { paymentId } = useParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [formFilters, setFormFilters] = useState({
        status: '',
        from: '',
        to: ''
    });
    const itemsPerPage = 10;

    useEffect(() => {
        if (paymentId) {
            fetchPaymentRefunds();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentId, currentPage]);

    const fetchPaymentRefunds = () => {
        const skip = (currentPage - 1) * itemsPerPage;
        const params = {
            count: itemsPerPage,
            skip,
            ...(formFilters.status && { status: formFilters.status }),
            ...(formFilters.from && { from: formFilters.from }),
            ...(formFilters.to && { to: formFilters.to })
        };
        getPaymentRefunds(paymentId, params);
    };

    const handleFilterChange = (e) => {
        setFormFilters({
            ...formFilters,
            [e.target.name]: e.target.value
        });
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchPaymentRefunds();
    };

    const handleClearFilters = () => {
        setFormFilters({
            status: '',
            from: '',
            to: ''
        });
        setCurrentPage(1);
        getPaymentRefunds(paymentId, { count: itemsPerPage, skip: 0 });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            processed: { variant: 'success', icon: <CheckCircle size={14} className="me-1" /> },
            pending: { variant: 'warning', icon: <Clock size={14} className="me-1" /> },
            failed: { variant: 'danger', icon: <XCircle size={14} className="me-1" /> }
        };
        const config = statusConfig[status?.toLowerCase()] || { variant: 'secondary', icon: null };
        return (
            <Badge bg={config.variant} className="d-flex align-items-center" style={{ width: 'fit-content' }}>
                {config.icon}
                {status || 'Unknown'}
            </Badge>
        );
    };

    const renderPagination = () => {
        if (!pagination || pagination.total <= itemsPerPage) return null;

        const totalPages = Math.ceil(pagination.total / itemsPerPage);
        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (startPage > 1) {
            items.push(
                <Button
                    key={1}
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    className="me-1"
                >
                    1
                </Button>
            );
            if (startPage > 2) {
                items.push(<span key="ellipsis-start" className="me-1">...</span>);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Button
                    key={number}
                    variant={number === currentPage ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => handlePageChange(number)}
                    className="me-1"
                >
                    {number}
                </Button>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<span key="ellipsis-end" className="me-1">...</span>);
            }
            items.push(
                <Button
                    key={totalPages}
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Button>
            );
        }

        return (
            <div className="d-flex justify-content-center align-items-center mt-4 gap-2">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    Previous
                </Button>
                {items}
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    Next
                </Button>
            </div>
        );
    };

    if (!paymentId) {
        return (
            <div className="container-fluid py-4">
                <Alert variant="warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Payment ID is required to view refunds.
                </Alert>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <Link 
                        to="/inventory/v1.2/refunds"
                        className="btn btn-outline-primary mb-3"
                    >
                        <ArrowLeft size={16} className="me-2" />
                        Back to Refunds
                    </Link>
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                            <h2 className="text-primary mb-1">
                                <CreditCard size={32} className="me-3" />
                                Payment Refunds
                            </h2>
                            <p className="text-muted mb-0">
                                Refunds for Payment ID: <code>{paymentId}</code>
                            </p>
                        </div>
                        <Button
                            variant="outline-primary"
                            onClick={fetchPaymentRefunds}
                            disabled={loading}
                        >
                            <RefreshCw size={16} className="me-2" />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" dismissible>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Filters */}
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">
                            <Filter size={18} className="me-2" />
                            Filters
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleFilterSubmit}>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={formFilters.status}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="processed">Processed</option>
                                            <option value="pending">Pending</option>
                                            <option value="failed">Failed</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>From Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="from"
                                            value={formFilters.from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>To Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="to"
                                            value={formFilters.to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex gap-2">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Searching...
                                        </>
                                    ) : (
                                        <>
                                            <Search size={16} className="me-2" />
                                            Apply Filters
                                        </>
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    onClick={handleClearFilters}
                                    disabled={loading}
                                >
                                    Clear
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>

                {/* Statistics */}
                {pagination && (
                    <Row className="mb-4">
                        <Col md={4}>
                            <Card className="shadow-sm border-0 bg-primary text-white">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{pagination.total || 0}</div>
                                            <div className="small">Total Refunds</div>
                                        </div>
                                        <IndianRupee size={28} className="opacity-75" />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm border-0 bg-info text-white">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{pagination.count || 0}</div>
                                            <div className="small">Showing</div>
                                        </div>
                                        <FileText size={28} className="opacity-75" />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm border-0 bg-success text-white">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{pagination.skip || 0}</div>
                                            <div className="small">Skipped</div>
                                        </div>
                                        <Calendar size={28} className="opacity-75" />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading payment refunds...</p>
                        </div>
                    </div>
                )}

                {/* Refunds Table */}
                {!loading && (
                    <Card className="shadow-sm">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-table me-2"></i>
                                Payment Refunds
                            </h5>
                            {pagination && (
                                <Badge bg="light" text="dark" className="fs-6">
                                    {pagination.total || 0} refund{pagination.total !== 1 ? 's' : ''}
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body className="p-0">
                            {paymentRefunds && paymentRefunds.length > 0 ? (
                                <>
                                    <div className="table-responsive">
                                        <Table striped hover responsive className="mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Refund ID</th>
                                                    <th>Amount (₹)</th>
                                                    <th>Status</th>
                                                    <th>Speed</th>
                                                    <th>Receipt</th>
                                                    <th>Created At</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paymentRefunds.map((refund) => (
                                                    <tr key={refund.refundId}>
                                                        <td>
                                                            <Link
                                                                to={`/inventory/v1.2/refunds/${refund.refundId}`}
                                                                className="text-decoration-none fw-bold text-primary"
                                                            >
                                                                {refund.refundId}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-success">
                                                                ₹{refund.amount?.toLocaleString() || '0.00'}
                                                            </span>
                                                            {refund.currency && (
                                                                <div className="small text-muted">{refund.currency}</div>
                                                            )}
                                                        </td>
                                                        <td>{getStatusBadge(refund.status)}</td>
                                                        <td>
                                                            <Badge bg="info">
                                                                {refund.speed || 'normal'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            {refund.receipt ? (
                                                                <code>{refund.receipt}</code>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <Calendar size={14} className="me-1 text-muted" />
                                                                {formatDate(refund.createdAt)}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                as={Link}
                                                                to={`/inventory/v1.2/refunds/${refund.refundId}`}
                                                                variant="outline-primary"
                                                                size="sm"
                                                            >
                                                                <Eye size={14} className="me-1" />
                                                                View
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                    {renderPagination()}
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <IndianRupee size={48} className="text-muted mb-3" />
                                    <p className="text-muted">No refunds found for this payment.</p>
                                    {filters && Object.keys(filters).length > 0 && (
                                        <Button
                                            variant="outline-primary"
                                            onClick={handleClearFilters}
                                            className="mt-2"
                                        >
                                            Clear Filters
                                        </Button>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </div>
        </Fragment>
    );
};

PaymentRefunds.propTypes = {
    getPaymentRefunds: PropTypes.func.isRequired,
    paymentRefunds: PropTypes.array,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    pagination: PropTypes.object,
    filters: PropTypes.object
};

const mapStateToProps = state => ({
    paymentRefunds: state.refund.paymentRefunds,
    loading: state.refund.loading,
    error: state.refund.error,
    pagination: state.refund.pagination,
    filters: state.refund.filters
});

export default connect(mapStateToProps, { getPaymentRefunds })(PaymentRefunds);

